/* eslint @typescript-eslint/no-explicit-any: 0 */

export const MEDIUM_BREAKPOINT = 768;
export const LARGE_BREAKPOINT = 1024;

export const RECORD_TYPES = {
  ADVERT: 'advert',
  AFFILIATE: 'affiliate',
  ARTICLE: 'article',
  AUTHOR: 'author',
  BRAND: 'brand',
  BUCKET: 'bucket',
  COLLECTION: 'collection',
  PRODUCT: 'product',
  PROMOTION: 'promotion',
  RAFFLE: 'raffle',
  SUBBRAND: 'sub-brand'
};

export const IMAGE_TYPES = {
  AFFILIATE_LOGO: 'AFFILIATE_LOGO',
  AUTHOR: 'AUTHOR',
  BRAND_LOGO: 'BRAND_LOGO',
  CAROUSEL_THUMB: 'CAROUSEL_THUMB',
  CLOTHING_HERO: 'CLOTHING_HERO',
  COLOUR_PICKER: 'COLOUR_PICKER',
  FULL_BLEED: 'FULL_BLEED',
  NEWS_RAFFLE_PRODUCT_SLIDE: 'NEWS_RAFFLE_PRODUCT_SLIDE',
  NEWS_RAFFLE_RESULT: 'NEWS_RAFFLE_RESULT',
  PRODUCT_HERO: 'PRODUCT_HERO',
  PRODUCT_RESULT: 'PRODUCT_RESULT',
  SEARCH_RESULT: 'SEARCH_RESULT'
};

export const RECORD_SUBTYPES = {
  CLOTHING: 'clothing',
  SNEAKER: 'sneaker'
};

export const RESPONSIVE_IMAGE_BREAKPOINTS: Record<number, string> = {
  0: '(max-width: 414px)',
  1: '(min-width: 415px) and (max-width: 768px)',
  2: '(min-width: 769px) and (max-width: 1024px)',
  3: '(min-width: 1025px)'
};

export const SEARCH_RESULTS_IMAGE_SIZES = [150, 150, 150, 150];

export const getImageSizes = (type: string) => {
  let sizes;

  switch (type) {
    case IMAGE_TYPES.SEARCH_RESULT:
      sizes = SEARCH_RESULTS_IMAGE_SIZES;
      break;

    default:
      break;
  }

  return sizes;
};

export const getMedia = (type: string) => {
  const sizes = getImageSizes(type) || [];

  return sizes.map((size, index) => ({
    media: RESPONSIVE_IMAGE_BREAKPOINTS[index],
    size
  }));
};

export const getBiggestSize = (responsiveImageType: string) => {
  let size = 0;

  switch (responsiveImageType) {
    case IMAGE_TYPES.AFFILIATE_LOGO:
    case IMAGE_TYPES.AUTHOR:
      size = 150;
      break;

    case IMAGE_TYPES.BRAND_LOGO:
      size = 200;
      break;

    default:
      size = Math.max(...(getImageSizes(responsiveImageType) || []));
      break;
  }

  return size;
};

export const siteConfig = {
  appleStore:
    'https://itunes.apple.com/gb/app/the-sole-supplier/id1436712793?mt=8',
  name: 'The Sole Supplier',
  socialMediaLinks: {
    sneaker: {
      crunchbase: 'https://www.crunchbase.com/organization/the-sole-supplier',
      facebook: 'https://www.facebook.com/thesolesupplier',
      instagram: 'https://www.instagram.com/thesolesupplier/',
      linkedin: 'https://www.linkedin.com/company/thesolesupplier/',
      tiktok: 'https://www.tiktok.com/@thesolesupplier',
      twitter: 'https://twitter.com/thesolesupplier',
      youtube: 'https://www.youtube.com/user/thesolesupplier'
    }
  }
};

/**
 * input (noCase): tHe sTrIng
 * output (hyphenCase): the-string
 */
export const hyphenCase = (...strings: Array<string>) => {
  const filteredParams = strings
    .filter((string) => typeof string === 'string' && string.trim() !== '')
    .join(' ');

  // Replace anything not a letter, number or dash with a dash.
  // Replace lowerCase followed by upperCase and sequential upperCase followed by lowerCase with a dash.
  // Replace multiple sequential dashes and any trailing with nothing.
  return filteredParams
    .replace(
      /([^a-zA-Z0-9,^]+)|([a-z])([A-Z])|([A-Z])([A-Z][a-z])|(-{1,})/g,
      '$2$4-$3$5'
    )
    .replace(/^[\s-]{1,}|[\s-]{1,}$/g, '')
    .toLowerCase();
};

export const bemKeyCase = hyphenCase;

export const isColourDark = (colour = '#000000') => {
  const hexCode = colour.substring(1); // strip #
  const rgb = parseInt(hexCode, 16); // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff; // extract red
  const g = (rgb >> 8) & 0xff; // extract green
  const b = (rgb >> 0) & 0xff; // extract blue
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

  return luma < 40;
};

export const isSSR = () =>
  typeof window === 'undefined' || window === undefined;

export const getIsBelowLargeBreakpoint = (
  windowWidth: number,
  isMobileRequest: boolean
) => {
  if (!windowWidth) return isMobileRequest;

  return windowWidth && windowWidth < LARGE_BREAKPOINT;
};

export const getIsMediumBreakpoint = (
  windowWidth: number,
  isMobileRequest: boolean
) => {
  if (!windowWidth) return isMobileRequest;

  return windowWidth && windowWidth >= MEDIUM_BREAKPOINT;
};

export const getIsLargeBreakpoint = (
  windowWidth: number,
  isMobileRequest: boolean
) => {
  if (!windowWidth) return isMobileRequest;

  return windowWidth && windowWidth >= LARGE_BREAKPOINT;
};

export function bem(baseClassName: string, modifiers: any) {
  if (!modifiers) {
    return baseClassName;
  }

  let modifiersList;

  if (Array.isArray(modifiers)) {
    modifiersList = Object.values(modifiers).filter((val) =>
      typeof val === 'number' ? true : val
    );
  } else if (typeof modifiers === 'string') {
    modifiersList = [modifiers];
  } else {
    modifiersList = Object.keys(modifiers).filter((key) =>
      typeof modifiers[key] === 'number' ? true : modifiers[key]
    );
  }

  modifiersList = modifiersList.map(
    (key) => `${baseClassName}--${bemKeyCase(key.toString())}`
  );
  modifiersList.unshift(baseClassName);

  return modifiersList.join(' ');
}

export const getAppRoot = () => {
  if (!isSSR()) {
    return document.getElementById('root');
  }
};

export const modalFrameSemantic = {
  BLUE: 'blue',
  CONCRETE: 'concrete',
  TRANSPARENT: 'transparent',
  WHITE: 'white'
};

export const modalFramePosition = {
  DEFAULT: 'default',
  RIGHT: 'right'
};

export const logoIcons: Record<string, any> = {
  adidas: 'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/adidas.png',
  asics: 'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/asics.png',
  carhartt:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/carhartt.png',
  coleBuxton:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/cole_buxton.png',
  converse:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/converse.png',
  corteiz:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/corteiz.png',
  fearOfGod:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/fear_of_god_essentials.png',
  jordan: 'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/jordan.png',
  montirex:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/montirex.png',
  newBalance:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/new_balance.png',
  nike: 'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/nike.png',
  represent:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/represent.png',
  stussy: 'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/stussy.png',
  theNorthFace:
    'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/the_north_face.png',
  ugg: 'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/ugg.png',
  uniqlo: 'https://cms-cdn.thesolesupplier.co.uk/bolt/assets/logos/uniqlo.png'
};

export const replaceAll = (str = '', mapWith: Record<string, string> = {}) => {
  var keys = new RegExp(Object.keys(mapWith).join('|'), 'gi');

  return str.replace(keys, (matched) => mapWith[matched]!);
};

const getInsightsFields = (searchResults = {}, hit = {}) => {
  const { index, queryID }: { index?: number; queryID?: string } =
    searchResults;
  const {
    __position: position,
    objectID
  }: { __position?: number; objectID?: string } = hit;

  return {
    index,
    queryID,
    ...(objectID ? { objectIDs: [objectID] } : {}),
    ...(position ? { positions: [position] } : {})
  };
};

export { getInsightsFields };
